import { untrack } from 'svelte';
import {
	NewsletterFunnelAction,
	NewsletterFunnelStage,
	NewsletterMixpanelEvents,
	NewsletterStartTrigger,
} from '../../constants/mixpanel-events';
import { hashPii } from '$global/utils/hashPii/hashPii';
import { track } from '$global/utils/track';

// Getters rather than plain values: several call sites derive `provenance` and
// `listId` reactively, and a snapshot taken at init would go stale.
interface NewsletterFunnelOptions {
	provenance: () => string;
	startTrigger: () => NewsletterStartTrigger;
	// Optional: the logged-in opt-in dialog toggles a user preference rather than
	// subscribing to a specific list.
	newsletterListId?: () => number | number[];
	/**
	 * Optional visibility signal for forms whose component mounts before the user
	 * can see them — a dialog rendered unconditionally by its parent, gated
	 * internally on an open store. When given, `Mount` means "when this turns
	 * true" rather than "when the component initialises".
	 */
	isOpen?: () => boolean;
	/**
	 * Who hit the failure. Read at emit time rather than passed to
	 * `onSubmitError`, so every call site attaches it the same way.
	 */
	email?: () => string | undefined;
	/**
	 * Defaults to `Subscribe`. Unsubscribes share this event so that all
	 * newsletter failures are one query — signup conversion must therefore
	 * filter to `action: 'subscribe'`.
	 */
	action?: () => NewsletterFunnelAction;
}

/**
 * Per-form-instance funnel bookkeeping for the newsletter signup.
 *
 * Emits `started` (once), `failed` and `abandoned` on a single event, so that
 * attempt-to-success can be measured against the existing `NEWSLETTER/SUBMIT`
 * happy path. Call from component init — it registers an `$effect` teardown to
 * catch abandonment.
 */
export const createNewsletterFunnel = ({
	provenance,
	startTrigger,
	newsletterListId,
	isOpen,
	email,
	action,
}: NewsletterFunnelOptions) => {
	// Plain `let`, not `$state`: these are only read/written inside effects and
	// callbacks, and must not re-trigger the teardown effect below.
	let hasStarted = false;
	let hasSubmitted = false;
	let hasAbandoned = false;

	const trackStage = (
		stage: NewsletterFunnelStage,
		extraProps?: Record<string, unknown>
	) => {
		track(NewsletterMixpanelEvents.NewsletterFormFunnel, {
			stage,
			provenance: provenance(),
			start_trigger: startTrigger(),
			action: action ? action() : NewsletterFunnelAction.Subscribe,
			...(newsletterListId ? { newsletterListId: newsletterListId() } : {}),
			...extraProps,
		});
	};

	const start = () => {
		if (hasStarted) {
			return;
		}

		hasStarted = true;
		trackStage(NewsletterFunnelStage.Started);
	};

	// Abandonment is only meaningful for a form the user actually started — this
	// keeps the funnel balanced as started = abandoned + submitted, rather than
	// counting every scrolled-past footer as an abandon. Guarded against firing
	// twice for a dialog that is dismissed and then also destroyed.
	const abandon = () => {
		if (!hasStarted || hasSubmitted || hasAbandoned) {
			return;
		}

		hasAbandoned = true;
		trackStage(NewsletterFunnelStage.Abandoned);
	};

	$effect(() => {
		// `isOpen` is the one intentional reactive dependency: a dialog rendered
		// unconditionally by its parent mounts on page load, so visibility rather
		// than mount is what marks the start of a visit. Read untracked, the
		// trigger itself must not re-run this and emit a spurious abandon.
		const visible = isOpen ? isOpen() : true;

		if (untrack(startTrigger) === NewsletterStartTrigger.Mount && visible) {
			start();
		}

		// Closing ends the visit: emit the abandon now, then reset so that
		// reopening is tracked as a new one rather than being swallowed by the
		// already-started guard.
		if (isOpen && !visible) {
			untrack(abandon);
			hasStarted = false;
			hasSubmitted = false;
			hasAbandoned = false;
		}

		return () => {
			abandon();
		};
	});

	return {
		/**
		 * Marks intent for interaction-triggered forms. No-op once started, and
		 * for mount-triggered forms which have already fired on show.
		 */
		onInteraction: () => {
			if (startTrigger() === NewsletterStartTrigger.Interaction) {
				start();
			}
		},
		/**
		 * Explicit abandonment, for dialogs that stay mounted when dismissed and so
		 * would never trigger the teardown above.
		 */
		onDismiss: abandon,
		onSubmitSuccess: () => {
			// A submitted form is no longer abandonable, even though the component
			// stays mounted showing its success message.
			hasSubmitted = true;
		},
		onSubmitError: (error: unknown) => {
			// The form was attempted, so it is a failure rather than an abandon —
			// but a retry after this should still be able to succeed.
			start();

			// Hashed so the address never reaches OpenPanel in plain text (HB-1296).
			void hashPii(email?.()).then((hashedEmail) => {
				trackStage(NewsletterFunnelStage.Failed, {
					failure_reason:
						error instanceof Error ? error.message : String(error ?? 'unknown'),
					...(hashedEmail ? { email: hashedEmail } : {}),
				});
			});
		},
	};
};
